/**
 * QuoteLineEditor.jsx — l'editeur des lignes d'un devis ou d'une facture.
 *
 * Chaque ligne porte : la designation, la quantite, **l'unite**, le prix
 * unitaire HT, **sa remise** et **son regime de TVA**. Les trois dernieres
 * s'imprimaient deja sur le document et **ne se saisissaient nulle part** :
 * la colonne Remise ne pouvait donc jamais apparaitre, et l'unite se deduisait
 * toujours du mode de facturation.
 *
 * **Les listes ne sont pas ecrites ici** : elles viennent de
 * `GET /api/edition/referentiels`, donc des modules qui les tiennent. Deux
 * listes du meme referentiel finissent par ne pas proposer la meme chose, et
 * c'est l'ecran qui promet alors ce que le serveur refuse.
 *
 * **Deux rangees par ligne, et les intitules sur chacune.** Sept controles ne
 * tiennent pas sur une rangee de 820 px sans rendre les deux listes
 * illisibles ; et une rangee d'intitules posee une seule fois en tete ne
 * surmonterait pas les controles qu'elle nomme, puisque la designation occupe
 * la rangee du dessus. Un devis porte une a cinq lignes : mieux vaut repeter
 * cinq petits mots que laisser deviner si « 4 » est une quantite ou un prix.
 *
 * Purement presentationnel — ne detient aucun etat. C'est l'appelant qui tient
 * le tableau des lignes.
 */
import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

const CASE = 'h-9 px-2.5 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] ' +
  'text-[12.5px] focus:bg-white focus:border-[#4F46E5] outline-none w-full';
const INTITULE = 'block text-[10px] font-semibold text-[#8E8FA5] uppercase ' +
  'tracking-wide mb-0.5';

export default function QuoteLineEditor({
  lines, onAdd, onUpdate, onRemove,
  unites = [], regimesTva = [], uniteDeduite = '',
}) {
  const exigeUnMotif = (code) =>
    !!regimesTva.find((r) => r.code === code && r.exige_un_motif);

  return (
    <section data-testid="quote-line-editor">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide">
          Lignes de prestation
        </label>
        <button
          type="button"
          onClick={onAdd}
          className="text-[11.5px] font-semibold text-[#4F46E5] hover:underline inline-flex items-center gap-1"
          data-testid="quote-add-line"
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter une ligne
        </button>
      </div>

      <div className="space-y-2">
        {lines.map((ln, i) => (
          <div
            key={i}
            className="rounded-xl border border-[#EFF0F6] p-2.5 space-y-2"
            data-testid={`quote-line-${i}`}
          >
            {/* La designation prend la rangee : c'est le champ long. */}
            <div className="flex items-start gap-2">
              <input
                value={ln.label}
                onChange={(e) => onUpdate(i, 'label', e.target.value)}
                placeholder="Désignation de la prestation"
                className={CASE}
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                disabled={lines.length === 1}
                title="Retirer cette ligne"
                className="h-9 w-9 shrink-0 rounded-lg text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-30 flex items-center justify-center"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-2">
                <span className={INTITULE}>Qté</span>
                <input
                  type="number" min="1" step="1"
                  value={ln.quantity}
                  onKeyDown={(e) => { if (['.', ',', 'e', 'E', '-', '+'].includes(e.key)) e.preventDefault(); }}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d]/g, '');
                    onUpdate(i, 'quantity', raw === '' ? '' : parseInt(raw, 10));
                  }}
                  onBlur={(e) => {
                    const v = parseInt(e.target.value, 10);
                    onUpdate(i, 'quantity', isNaN(v) || v < 1 ? 1 : v);
                  }}
                  data-testid={`quote-line-qty-${i}`}
                  className={`${CASE} text-right`}
                />
              </div>

              {/* **Vide veut dire « comme le document »** et non « rien » : le
                  mode de facturation en dit assez pour imprimer, et c'est ce
                  que la premiere option annonce. */}
              <div className="col-span-2">
                <span className={INTITULE}>Unité</span>
                <select
                  value={ln.unit_code || ''}
                  onChange={(e) => onUpdate(i, 'unit_code', e.target.value || null)}
                  data-testid={`quote-line-unit-${i}`}
                  className={CASE}
                >
                  <option value="">
                    {uniteDeduite ? `${uniteDeduite} (du document)` : 'Non précisée'}
                  </option>
                  {unites.map((u) => (
                    <option key={u.code} value={u.code}>{u.libelle}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <span className={INTITULE}>PU HT €</span>
                <input
                  type="number" step="0.01" min="0"
                  value={ln.unit_price}
                  onChange={(e) => onUpdate(i, 'unit_price', e.target.value)}
                  className={`${CASE} text-right`}
                />
              </div>

              {/* La remise de la ligne se cumule avec celle du document, dans
                  cet ordre. Zero ne fait pas apparaitre la colonne. */}
              <div className="col-span-2">
                <span className={INTITULE}>Remise %</span>
                <input
                  type="number" step="0.01" min="0" max="100"
                  value={ln.discount_percent ?? 0}
                  onChange={(e) => onUpdate(i, 'discount_percent', e.target.value)}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    onUpdate(i, 'discount_percent',
                      !isFinite(v) || v < 0 ? 0 : Math.min(100, v));
                  }}
                  data-testid={`quote-line-discount-${i}`}
                  className={`${CASE} text-right`}
                />
              </div>

              <div className="col-span-4">
                <span className={INTITULE}>TVA</span>
                <select
                  value={ln.tva_regime || ''}
                  onChange={(e) => {
                    const code = e.target.value || null;
                    onUpdate(i, 'tva_regime', code);
                    // Un motif n'appartient qu'au regime qui l'exige : le
                    // garder sous un autre l'imprimerait sans raison.
                    if (!exigeUnMotif(code)) onUpdate(i, 'tva_mention', null);
                  }}
                  data-testid={`quote-line-tva-${i}`}
                  className={CASE}
                >
                  <option value="">Comme le document</option>
                  {regimesTva.map((r) => (
                    <option key={r.code} value={r.code}>{r.libelle}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* **« Exonéré » tout seul ne justifie rien** : le serveur refuse
                la ligne sans sa mention, et une facture a 0 % sans motif est
                irreguliere. On la demande donc ici plutot que de laisser
                arriver le refus. */}
            {exigeUnMotif(ln.tva_regime || '') && (
              <div>
                <span className={INTITULE}>Mention d'exonération (imprimée)</span>
                <input
                  value={ln.tva_mention || ''}
                  onChange={(e) => onUpdate(i, 'tva_mention', e.target.value)}
                  placeholder="Ex. : autoliquidation — article 283-2 du CGI"
                  data-testid={`quote-line-tva-mention-${i}`}
                  className={CASE}
                />
                {!String(ln.tva_mention || '').trim() && (
                  <p className="text-[10.5px] text-[#B45309] mt-1">
                    Sans mention, la ligne sera refusée : une facture sans taxe
                    doit dire pourquoi.
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
